import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { VehicleType } from "@app/enum/VehicleType.enum";
import { StreamaxRecordOverwrite } from "@app/main/modules/foundation/_enums/camera-configs/CameraConfigEnums";
import { ILocalTimeSettings } from "@app/main/modules/foundation/_models/_camera-configs/CameraConfigModels";
import { DataCacheService } from "@app/services/caching/data-cache.service";
import { ITimeZoneInfo } from "@app/userPreferences/model/ITimeZoneInfo";
import { Observable } from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class StreamaxCameraProfileService {

    private streamaxCameraProfilesCache: string = "streamaxCameraProfilesCache";

    constructor(
        private readonly dataCacheService: DataCacheService,
        private readonly http: HttpClient
    ) { }

    getProfiles(invalidateCache = false): Observable<IStreamaxCameraProfile[]> {
        return this.dataCacheService.getOrRequest(this.streamaxCameraProfilesCache, () => this.getProfilesFromDb(), { invalidateCache });
    }

    getProfilesFromDb(): Observable<IStreamaxCameraProfile[]> {
        return this.http.get<IStreamaxCameraProfile[]>(`/api/cameras/profiles`);
    }

    postProfile(profile: IStreamaxCameraProfile): Observable<IStreamaxCameraProfile> {
        return this.http.post<IStreamaxCameraProfile>(`/api/cameras/profiles/basic`, profile);
    }

    postTriggerOverride(profileId: number, customTriggers: ITriggerMetricsOverride): Observable<void> {
        return this.http.post<void>(`/api/cameras/profiles/${profileId}/triggeroverride`, customTriggers);
    }

    deleteTriggerOverride(profileId: number, vehicleType: VehicleType): Observable<void> {
        return this.http.delete<void>(`/api/cameras/profiles/${profileId}/triggeroverride/${vehicleType}`);
    }

    postActiveSetUpdate(profileId: number, activeSet: boolean): Observable<void> {
        return this.http.post<void>(`/api/cameras/profiles/${profileId}/active`, activeSet);
    }

    convertStreamaxTimezoneId(streamaxTimezoneId: string): Observable<{ timezoneId: string }> {
        return this.http.get<{ timezoneId: string }>(`/api/cameras/profiles/streamaxtimezoneid/convert`, {
            params: { streamaxTimezoneId }
        });
    }

    convertTimezoneInfoIdToStreamaxModel(timezoneInfoId: string): Observable<ILocalTimeSettings> {
        return this.http.get<ILocalTimeSettings>(`/api/cameras/profiles/internaltimezoneid/convert`, { params: { timezoneInfoId } });
    }

    getTriggerDefaults(vehicleType: VehicleType): Observable<ITriggerMetricsMap> {
        return this.http.get<ITriggerMetricsMap>(`/api/cameras/profiles/triggerdefaults/${vehicleType}`)
    }

    updateProfileToCache(profile: IStreamaxCameraProfile): void {
        this.dataCacheService.addOrUpdateItem(profile, this.streamaxCameraProfilesCache);
    }
}

export interface IStreamaxCameraProfile {
    AccountId: number;
    CreatedTime?: Date;
    UpdatedTime?: Date;
    Name?: string;
    IsActive: boolean;
    Id: number;
    CameraDeviceParameterProfileSettings: ICameraDeviceParameterProfileSettings;
}

export interface ICameraDeviceParameterProfileSettings {
    AudioEnabled: boolean;
    IgnitionDelay: number;
    CameraCoveredAlarm: boolean;
    VideoLossAlarm: boolean;
    RecordOverwrite: StreamaxRecordOverwrite;
    TimeSettings: ILocalTimeSettings;
    AlarmProfileMap: IAlarmProfileMap;

    // -----

    internalTimezoneInfo?: ITimeZoneInfo;
    updatedTimezoneInfo?: ITimeZoneInfo;
}

export interface IAlarmProfileMap {
    AlarmEnabled: boolean;
    HarshBrakingTriggerEnabled: boolean;
    HardAccelerationTriggerEnabled: boolean;
    HarshRightTurnTriggerEnabled: boolean;
    HarshLeftTurnTriggerEnabled: boolean;
    TriggerMetricOverrides?: ITriggerMetricsOverride[];
}

export interface ITriggerMetricsOverride {
    VehicleType: VehicleType;
    TriggerMetricsMap: ITriggerMetricsMap;
}

export interface ITriggerMetricsMap {
    HarshBrakingTriggerMetrics: ITriggerMetrics;
    HarshAccelerationTriggerMetrics: ITriggerMetrics;
    LeftTurnTriggerMappings: ITriggerMetrics;
    RightTurnTriggerMappings: ITriggerMetrics;
    GyroTriggers: IGyroTriggers;
}

export interface ITriggerMetrics {
    StartSpeed: number;
    EndSpeed: number;
    Offset: number;
    ReAlertTimeSeconds: number;
}

export interface IGyroTriggers {
    XTrigger: number;
    YTrigger: number;
    ZTrigger: number;
}
